import {
  Button,
  DatePicker,
  Form,
  type FormInstance,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
} from 'antd';
import { ReactElement } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { DefaultOptionType } from 'antd/lib/select';
import { HelpTooltip } from 'packages/lib/common-ui/helpTooltip';
import { OmnitagFilter } from 'packages/lib/api/omnitag/contract/omnitag-filters';
import { SearchQueryForm } from 'packages/omnitag/text-search/filters-form';

dayjs.extend(utc); // UTC is a plugin that doesn't come with dayjs by default

// Aug-11-2025 - Used Gemini 2.5 Agent to make filters dynamic (FiltersPanel)
// Aug-20-2025 - Used Gemini 2.5 Agent to add support for 'number' filters

const SHOW_SELECT_ALL_FOR_MORE_THAN = 5;

const constArrayToOptions = (c: string): DefaultOptionType => ({
  label: c,
  value: c,
});

export const FilterItem = ({
  filter,
  form,
}: {
  filter: OmnitagFilter;
  form: FormInstance<SearchQueryForm | Record<string, unknown>>;
}) => {
  // disableIfFieldIsFalsy, if present, is the name of another field to watch. If its value is falsy,
  // the current field should be disabled, allowing the linked field to control this one.
  const linkedFieldValue = Form.useWatch(
    filter.disableIfFieldIsFalsy ?? '',
    form
  );
  const isDisabled = filter.disableIfFieldIsFalsy ? !linkedFieldValue : false;

  const filterLabel = (
    <>
      {filter.displayName}
      {filter.tooltip && (
        <HelpTooltip
          body={<>{filter.tooltip}</>}
          iconStyle={{ fontSize: '16px', marginLeft: '2px' }}
        />
      )}
    </>
  );

  if (filter.filterType === 'select') {
    const options = filter.options.map((o) =>
      typeof o === 'string' ? constArrayToOptions(o) : o
    );
    return (
      <Form.Item
        label={filterLabel}
        name={filter.paramName}
        key={filter.paramName}
      >
        <Select
          disabled={isDisabled}
          options={options}
          mode={filter.mode}
          allowClear
          style={{ minWidth: 200 }}
          tokenSeparators={[',', ' ']}
          popupRender={(menu) =>
            renderSelectWithSelectAll(menu, options, form, filter.paramName)
          }
        />
      </Form.Item>
    );
  } else if (filter.filterType === 'text') {
    return (
      <Form.Item
        label={filterLabel}
        name={filter.paramName}
        key={filter.paramName}
      >
        <Input disabled={isDisabled} type="text" />
      </Form.Item>
    );
  } else if (filter.filterType === 'dateRange') {
    return (
      <Form.Item
        label={filterLabel}
        name={filter.paramName}
        key={filter.paramName}
      >
        <DatePicker.RangePicker disabled={isDisabled} />
      </Form.Item>
    );
  } else if (filter.filterType === 'number') {
    return (
      <Form.Item
        label={filterLabel}
        name={filter.paramName}
        key={filter.paramName}
      >
        <InputNumber
          disabled={isDisabled}
          min={filter.minValue}
          max={filter.maxValue}
          step={1 / Math.pow(10, filter.decimalPlaces)}
        />
      </Form.Item>
    );
  } else if (filter.filterType === 'toggle') {
    return (
      <Form.Item
        label={filterLabel}
        name={filter.paramName}
        key={filter.paramName}
        valuePropName="checked"
      >
        <Switch disabled={isDisabled} />
      </Form.Item>
    );
  }
  console.warn('FiltersPanel: Unknown filterType: ', filter);
  return null;
};

/** Custom popup for Antd's Select field that contains
 * Select All and Deselect All buttons. Shown when there are 9 or more items.
 */
const renderSelectWithSelectAll = (
  menu: ReactElement,
  options: DefaultOptionType[],
  form: FormInstance<SearchQueryForm | Record<string, unknown>>,
  paramName: string
): ReactElement => {
  if (options.length <= SHOW_SELECT_ALL_FOR_MORE_THAN) {
    return menu;
  }
  return (
    <div>
      {menu}
      <hr />
      <Space style={{ margin: '0.7em 0.7em 0.7em 0.7em' }}>
        <Button
          onClick={() =>
            form.setFieldValue(
              paramName,
              options.map((o) => o.value)
            )
          }
        >
          Select All
        </Button>
        <Button onClick={() => form.setFieldValue(paramName, [])}>
          Deselect All
        </Button>
      </Space>
    </div>
  );
};
